// Better-Auth thin client. We hit /api/auth/* directly with the same fetch
// helper instead of pulling @better-auth/react — keeps the dep tree small and
// the auth surface explicit.

import { apiFetch, apiJson } from './api.ts';
import type { MeUser } from './types.ts';

interface MeResponse {
  user: MeUser;
}

/** Resolve the current session via /api/me. Returns null when anonymous. */
export async function fetchMe(): Promise<MeUser | null> {
  const res = await apiFetch('/api/me');
  if (res.status === 401) return null;
  if (!res.ok) {
    throw new Error(`fetchMe failed: ${res.status}`);
  }
  const body = (await res.json()) as MeResponse;
  return body.user;
}

/** Send a magic-link sign-in email. callbackURL is where the API redirects after click. */
export async function requestMagicLink(email: string, callbackURL: string): Promise<void> {
  await apiJson('/api/auth/sign-in/magic-link', {
    method: 'POST',
    body: JSON.stringify({ email, callbackURL }),
  });
}

/** Sign out the current session. */
export async function signOut(): Promise<void> {
  await apiJson('/api/auth/sign-out', { method: 'POST', body: '{}' });
}

/** Update the current user's display name and/or avatar token. */
export async function updateMe(updates: { name?: string; image?: string | null }): Promise<MeUser> {
  const body = await apiJson<MeResponse>('/api/me', {
    method: 'PATCH',
    body: JSON.stringify(updates),
  });
  return body.user;
}
